import { apiClient } from "@/lib/api";
import i18n from "@/lib/i18n";
import type {
  CancelInvitationResponse,
  CreateUserPayload,
  CreateUserResponse,
  InvitePayload,
  PaginatedUsers,
  RemoveUserResponse,
  SendPasswordResetResponse,
  TeamMember,
  TeamMemberStatus,
  User,
  UserUpdatePayload,
} from "@/types/users";

export interface GetUsersParams {
  page?: number;
  page_size?: number;
  search?: string;
  role?: string;
  /**
   * Team status filter (TASK-5093). Absent → no status filtering (the driver
   * pickers rely on that). An unknown value is a 400 `VALIDATION_ERROR`.
   */
  status?: TeamMemberStatus;
}

/**
 * Request config that tells the server which language the dispatcher's UI is
 * in, so the email it sends (invitation, password reset) arrives in that
 * language (Sprint 218, Q9).
 *
 * Sent EXPLICITLY from the i18n instance rather than left to the browser's own
 * header: the browser reports its install language, not the language picked in
 * the app (same rationale as `exportRoutePdf`). Per-request, not on the shared
 * client, so only the endpoints that mail someone carry it.
 */
function emailLanguageConfig() {
  return { headers: { "Accept-Language": i18n.language || "ru" } };
}

/**
 * Fetches a paginated list of tenant users.
 * GET /api/v1/users/
 *
 * Sprint 218: rows carry `status`, `invitation_expired` and the caller's
 * capability flags; the envelope adds `status_counts`, `team_total` and
 * `seats`. Pass `status` to filter by team status.
 */
export async function listUsers(
  params?: GetUsersParams,
): Promise<PaginatedUsers> {
  const response = await apiClient.get<PaginatedUsers>("/v1/users/", {
    params,
  });
  return response.data;
}

/** The server's `max_page_size` (FlexiblePageNumberPagination). */
const MAX_PAGE_SIZE = 200;
/** Safety stop for {@link listAllUsers}: 50 pages × 200 = 10 000 users. */
const MAX_PAGES = 50;

/**
 * Fetches EVERY page of `GET /api/v1/users/` for the given filters and returns
 * the concatenated rows (UR-1: a one-page picker silently dropped drivers past
 * the page size). Follows `next` until it is null, at the server's maximum
 * page size, with a hard page cap so a misbehaving server cannot loop forever.
 */
export async function listAllUsers(
  params?: Omit<GetUsersParams, "page" | "page_size">,
): Promise<TeamMember[]> {
  const rows: TeamMember[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const data = await listUsers({
      ...params,
      page,
      page_size: MAX_PAGE_SIZE,
    });
    rows.push(...data.results);
    if (!data.next) break;
  }
  return rows;
}

/**
 * Fetches a single tenant user by ID.
 * GET /api/v1/users/{id}/
 */
export async function getUser(id: number): Promise<TeamMember> {
  const response = await apiClient.get<TeamMember>(`/v1/users/${id}/`);
  return response.data;
}

/** 201 body of `POST /users/invite/` — the raw token is never returned (BUG-702). */
export interface InviteUserResponse {
  detail: string;
  invitation_id: number;
  expires_at: string;
}

/**
 * Invites a new user to the tenant.
 * POST /api/v1/users/invite/
 *
 * Sends `Accept-Language` so the invitation email is in the UI language.
 * Refusals: 400 `AUTH_EMAIL_TAKEN` (on `email`), 409 `QUOTA_EXCEEDED` with
 * `extra_data: {resource: "users", limit, current, used}`.
 */
export async function inviteUser(
  payload: InvitePayload,
): Promise<InviteUserResponse> {
  const response = await apiClient.post<InviteUserResponse>(
    "/v1/users/invite/",
    payload,
    emailLanguageConfig(),
  );
  return response.data;
}

/**
 * Partially updates an existing user.
 * PATCH /api/v1/users/{id}/
 *
 * Sends `Accept-Language`: changing an invited member's `email` re-sends the
 * invitation to the new address, and that email follows the UI language.
 * Refusals: `AUTH_EMAIL_TAKEN`, `USER_ADMIN_ROLE_READ_ONLY`,
 * `USER_CANNOT_CHANGE_OWN_ROLE`, `USER_CANNOT_DISABLE_SELF`,
 * `USER_LAST_COMPANY_ADMIN`, `QUOTA_EXCEEDED`, `ACCOUNT_DELETION_ALREADY_ERASED`.
 */
export async function updateUser(
  id: number,
  payload: UserUpdatePayload,
): Promise<User> {
  const response = await apiClient.patch<User>(
    `/v1/users/${id}/`,
    payload,
    emailLanguageConfig(),
  );
  return response.data;
}

/**
 * Disables («Отключить») a user by ID — NOT a delete, see {@link removeUser}.
 * DELETE /api/v1/users/{id}/
 *
 * Also spends unused invitation links, ends every web and mobile session and
 * frees the seat. Refuses yourself (`USER_CANNOT_DISABLE_SELF`) and the last
 * active company_admin (`USER_LAST_COMPANY_ADMIN`).
 */
export async function deactivateUser(id: number): Promise<void> {
  await apiClient.delete(`/v1/users/${id}/`);
}

/**
 * Re-activates («Включить») a previously disabled user by ID.
 * POST /api/v1/users/{id}/activate/
 * Idempotent — activating an already-active user succeeds. Returns the full
 * updated user so callers may patch the row without an extra fetch.
 * Takes a seat again: 409 `QUOTA_EXCEEDED` when the plan is full.
 */
export async function activateUser(id: number): Promise<User> {
  const response = await apiClient.post<User>(`/v1/users/${id}/activate/`);
  return response.data;
}

/**
 * Resends an invitation email for a pending (inactive) user.
 * POST /api/v1/users/invitations/{invitationId}/resend/
 *
 * Sends `Accept-Language` so the email is in the UI language.
 */
export async function resendInvitation(invitationId: number): Promise<void> {
  await apiClient.post(
    `/v1/users/invitations/${invitationId}/resend/`,
    undefined,
    emailLanguageConfig(),
  );
}

/** 200 body of the tenant-scoped, user-keyed invite route. */
export interface InviteExistingUserResponse {
  detail: string;
  invitation_id: number;
  expires_at: string;
}

/**
 * Mints a fresh password-setup invitation for an EXISTING team member.
 * POST /api/v1/users/{id}/invite/
 *
 * The tenant-scoped, USER-keyed counterpart of `resendInvitation`, which is
 * keyed on an invitation row. Needed for a member who has NO invitation to
 * resend — typically a driver reactivated after a self-service deletion request,
 * whose original invitation was consumed the day they first set a password.
 *
 * Roles: `company_admin` and `superadmin` (the same
 * `IsCompanyAdminOrSuperAdmin` gate as the rest of `UserViewSet`'s write
 * actions). A `dealer` gets 403 here and must use the admin-surface
 * `sendUserInvite` in `@/api/admin` instead.
 *
 * Refuses with 400 `ADMIN_INVITATION_ALREADY_VALID` when the member already
 * holds an unused, unexpired invitation. Another tenant's user is 404, never
 * 403 — existence is not leaked.
 *
 * Sends `Accept-Language` so the email is in the UI language.
 */
export async function inviteExistingUser(
  id: number,
): Promise<InviteExistingUserResponse> {
  const response = await apiClient.post<InviteExistingUserResponse>(
    `/v1/users/${id}/invite/`,
    undefined,
    emailLanguageConfig(),
  );
  return response.data;
}

/**
 * Deletes («Удалить») a team member, history-safe (Sprint 218).
 * POST /api/v1/users/{id}/remove/
 *
 * Nothing references the member → the row is deleted; otherwise it is kept as
 * an anonymized tombstone so routes, stops and the audit log stay valid.
 * Refusals: 400 `USER_CANNOT_DELETE_SELF`, 409 `USER_LAST_COMPANY_ADMIN`.
 */
export async function removeUser(id: number): Promise<RemoveUserResponse> {
  const response = await apiClient.post<RemoveUserResponse>(
    `/v1/users/${id}/remove/`,
  );
  return response.data;
}

/**
 * Cancels («Отменить приглашение») an invited member's pending invitation.
 * POST /api/v1/users/{id}/cancel-invitation/
 *
 * Spends every link and removes the pending member (or keeps them disabled
 * when something references them). 409 `USER_NOT_INVITED` otherwise.
 */
export async function cancelInvitation(
  id: number,
): Promise<CancelInvitationResponse> {
  const response = await apiClient.post<CancelInvitationResponse>(
    `/v1/users/${id}/cancel-invitation/`,
  );
  return response.data;
}

/**
 * Emails an active member a 1-hour password-reset link («Сбросить пароль»).
 * POST /api/v1/users/{id}/send-password-reset/
 *
 * Sends `Accept-Language` so the email is in the UI language. Reveals and ends
 * nothing until the member sets the new password. Refusals: 409
 * `USER_NOT_ACTIVE`, 429 `AUTH_RESET_THROTTLED` (4th link within an hour).
 */
export async function sendPasswordReset(
  id: number,
): Promise<SendPasswordResetResponse> {
  const response = await apiClient.post<SendPasswordResetResponse>(
    `/v1/users/${id}/send-password-reset/`,
    undefined,
    emailLanguageConfig(),
  );
  return response.data;
}

/**
 * Creates a new active user directly with a password.
 * POST /api/v1/users/create/
 * If payload.password is empty string, it is stripped before sending so the
 * backend auto-generates a secure password and returns it in generated_password.
 */
export async function createUser(
  payload: CreateUserPayload,
): Promise<CreateUserResponse> {
  const body: CreateUserPayload = { ...payload };
  if (!body.password) delete body.password;
  const response = await apiClient.post<CreateUserResponse>(
    "/v1/users/create/",
    body,
  );
  return response.data;
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  detail: string;
}

/**
 * Changes the authenticated user's own password.
 * POST /api/v1/users/change-password/
 * Tokens are not rotated — the user stays logged in.
 */
export async function changePassword(
  payload: ChangePasswordPayload,
): Promise<ChangePasswordResponse> {
  const response = await apiClient.post<ChangePasswordResponse>(
    "/v1/users/change-password/",
    payload,
  );
  return response.data;
}
